import { CheckIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/solid';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useProjectTemplates } from '../../../../../hooks/ReactQueryHooks/useProjectTemplateQuery';
import { projectsSelector } from '../../../../../store';
import { setProjectTemplate } from '../../../../../store/Projects/Project/ProjectTemplate';
import { formatDate3 } from '../../../../../utils';
import Button from '../../../../UI/Button';
import styles from '../Form.module.css';

interface ProjectTemplateInfoProps {
  handleProgress: (type: string) => void;
}

const ProjectTemplateInfo = ({ handleProgress }: ProjectTemplateInfoProps) => {
  const dispatch = useDispatch();
  const [params, setParams] = useState<any>({
    perPage: 10,
    page: 1,
  });

  const fetchProjectTemplates = useProjectTemplates({
    params,
  });
  const projectTemplates = fetchProjectTemplates?.data?.data;
  const projectTemplatesCount = fetchProjectTemplates?.data?.meta?.count;
  const pageCount = Math.floor(projectTemplatesCount / params.perPage);
  const { Project } = useSelector(projectsSelector);
  const selectedProjectTemplate = Project.ProjectTemplate;

  const handleTemplateSelect = (template: any) => {
    dispatch(
      setProjectTemplate({
        id: template.id,
        name: template.name,
      })
    );
  };

  const backHandler = () => {
    handleProgress('BACK');
  };

  const forwardHandler = () => {
    handleProgress('FORWARD');
  };

  const projectTemplatesPageHandler = (type: 'PREV' | 'NEXT') => {
    if (type === 'NEXT' && pageCount !== params.page) {
      setParams((prevState: { perPage: number; page: number }) => ({
        ...prevState,
        page: prevState.page + 1,
      }));
    }

    if (type === 'PREV' && params.page !== 1) {
      setParams((prevState: { perPage: number; page: number }) => ({
        ...prevState,
        page: prevState.page - 1,
      }));
    }
  };

  return (
    <>
      <h2 className={`mb-0 ${styles['form-title']}`}>Project Template</h2>
      <div className='grid grid-cols-12 gap-2 mt-5'>
        {projectTemplates?.map((temp: any) => (
          <div
            key={temp.id}
            className={`col-span-6 p-4 rounded cursor-pointer hover:bg-green-50 relative ${
              temp.id === selectedProjectTemplate.id ? 'bg-green-50' : 'bg-neutralColor-lightest'
            }`}
            onClick={() => handleTemplateSelect(temp)}
          >
            <h3 className='font-bold truncate'>{temp.name}</h3>
            {temp.id === selectedProjectTemplate.id && (
              <span className='text-primaryColor-normal absolute top-2 right-2'>
                <CheckIcon className='h-5 w-5' />
              </span>
            )}
            <p className='truncate'>{temp.description}</p>
            <p className='italic text-xs'>Created by {temp.createdBy.name}</p>
            <p className='italic text-xs'>Last updated on {formatDate3(temp.updatedAt)}</p>
          </div>
        ))}
      </div>
      {pageCount > 1 && (
        <div className='w-full flex gap-2 justify-end items-center mt-2'>
          <span
            className={`flex justify-center items-center cursor-pointer text-white p-1 rounded ${
              params.page === 1 ? 'bg-neutralColor-light' : 'bg-primaryColor-normal'
            }`}
            onClick={() => projectTemplatesPageHandler('PREV')}
          >
            <ChevronLeftIcon className='h-5 w-5' />
          </span>
          <span
            className={`flex justify-center items-center cursor-pointer text-white  p-1 rounded ${
              params.page === pageCount ? 'bg-neutralColor-light' : 'bg-primaryColor-normal'
            }`}
            onClick={() => projectTemplatesPageHandler('NEXT')}
          >
            <ChevronRightIcon className='h-5 w-5' />
          </span>
        </div>
      )}
      <div className='w-full flex justify-end gap-4 mt-5'>
        <Button type='button' btnStyle='btn-neutral' label='Previous' onClick={backHandler} />
        <Button type='button' btnStyle='btn-primary' label='Next' onClick={forwardHandler} />
      </div>
    </>
  );
};

export default ProjectTemplateInfo;
